import MDButton from "#components/MDButton/index.js";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Checkbox,
  CircularProgress,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import MDTypography from "components/MDTypography";
import Dialog from "components/NoScrollDialog";
import { useLanguage } from "contexts/language.context";
import { useEffect, useRef, useState } from "react";
import auditsService from "services/audits-service";
import useDebouncedOnChange from "utils/useDebounceOnChange";
import useObjectState from "utils/useObjectState";
import { PENDING_STATUS_ID } from "../../../../const";
import { transformToTime } from "../utils";
import LoadingButton from "#components/LoadingButton/index.js";

async function downloadPDF(url, filename, onProgress) {
  function saveBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  }
  const response = await fetch(url);
  if (!response.ok)
    throw new Error(`HTTP ${response.status} ${response.statusText}`);

  const totalHeader = response.headers.get("content-length");
  const total = totalHeader ? parseInt(totalHeader, 10) : 0;

  if (!response.body || !response.body.getReader) {
    const blob = await response.blob();
    onProgress?.({ percent: 100, indeterminate: false });
    saveBlob(blob, filename);
    return;
  }

  const reader = response.body.getReader();
  const chunks = [];
  let loaded = 0;

  if (total === 0) onProgress?.({ indeterminate: true });

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    loaded += value.byteLength;

    if (total > 0) {
      const percent = Math.max(
        0,
        Math.min(100, Math.round((loaded / total) * 100)),
      );
      onProgress?.({ percent, indeterminate: false });
    } else {
      onProgress?.({ indeterminate: true });
    }
  }

  const blob = new Blob(chunks, { type: "application/pdf" });
  onProgress?.({ percent: 100, indeterminate: false });
  saveBlob(blob, filename);
}

export default function ReportDialog({ isOpen, audit, summaryGroups }) {
  const { label } = useLanguage();

  const [options, changeOptions, , resetOptions] = useObjectState({
    includeTime: "include",
    includeType: true,
    includeAssignee: true,
    includeTypeOfVisit: "Dine-in",
    photosPerRow: 1,
  });
  const [loading, changeLoading, , resetLoading] = useObjectState({
    html: true,
  });
  const { value, handleChange } = useDebouncedOnChange(
    options.includeTypeOfVisit,
    500,
  );

  const [html, setHtml] = useState("");
  const [open, setOpen] = useState(false);
  const previewRequest = useRef(0);

  useEffect(() => {
    if (!isOpen || !open || !audit?.id) return;
    const requestId = ++previewRequest.current;
    changeLoading("html", true);
    const generateHtml = async () => {
      try {
        const sentSummaryGroups = Object.keys(summaryGroups)
          .filter((key) => summaryGroups[key])
          .map((key) => Number(key) || String(key));

        const query = {
          includeTime: options.includeTime ? "include" : "",
          includeType: options.includeType ? "include" : "",
          includeAssignee: options.includeAssignee ? "include" : "",
          typeOfVisit: options.includeTypeOfVisit,
        photosPerRow: options.photosPerRow,
          html: "true",
        };

        const body = { summaryGroups: sentSummaryGroups };
        const [response, data] =
          await auditsService.generateAssessmentReportPreview(
            audit.id,
            query,
            body,
          );

        if (requestId !== previewRequest.current) return;
        if (response.ok) {
          setHtml(data);
        } else {
          changeLoading("html", false);
        }
      } catch (err) {
        if (requestId === previewRequest.current) {
          console.error("Error generating report link:", err);
          changeLoading("html", false);
        }
      }
    };
    generateHtml();
    return () => {
      previewRequest.current++;
    };
  }, [isOpen, open, audit?.id, JSON.stringify(options)]);

  const generatePDF = async () => {
    if (!audit?.id) return;

    changeLoading("download", true);
    try {
      const sentSummaryGroups = Object.keys(summaryGroups)
        .filter((key) => summaryGroups[key])
        .map((key) => Number(key) || String(key));

      const query = {
        includeTime: options.includeTime ? "include" : "",
        includeType: options.includeType ? "include" : "",
        includeAssignee: options.includeAssignee ? "include" : "",
        typeOfVisit: options.includeTypeOfVisit,
          photosPerRow: options.photosPerRow,
      };

      const body = {
        summaryGroups: sentSummaryGroups,
      };

      const [response, data] = await auditsService.generateAssessmentReportLink(
        audit.id,
        query,
        body,
      );

      if (response.ok) {
        const dateText = new Date(audit.firstAnsweredAt || audit.date)
          .toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })
          .replace(/\//g, "_");

        const filename = `${audit.branch.branchGroup.name}-${audit.branch.name}-ifpc360-mystery-${dateText}.pdf`;

        await downloadPDF(data.url, filename);
      }
    } catch (err) {
      console.error("Error generating report link:", err);
    }
    changeLoading("download", false);
  };

  useEffect(() => {
    if (!isOpen) return;
    setOpen(true);
    setHtml("");

    resetOptions();
    resetLoading();
  }, [isOpen, audit?.auditTypeId]);

  const handleClose = () => setOpen(false);

  const isTimeDisabled =
    !audit?.firstAnsweredAt ||
    audit?.auditLogStatuses?.findIndex(
      (item) => item.statusId !== PENDING_STATUS_ID,
    ) === -1;

  const handleTimeCheckbox = (event) => {
    if (event.target.checked) {
      const statusIndex = audit?.auditLogStatuses?.findIndex(
        (item) => item.statusId !== PENDING_STATUS_ID,
      );

      if (statusIndex !== -1) {
        changeOptions(
          "includeTime",
          transformToTime(audit.auditLogStatuses[statusIndex].time),
        );
      } else {
        changeOptions("includeTime", "");
      }
    } else {
      changeOptions("includeTime", "");
    }
  };

  return (
    <Dialog
      maxWidth={false}
      open={open}
      PaperProps={{
        sx: {
          width: "min(1100px, calc(100vw - 64px))",
          height: "calc(100vh - 64px)",
          maxHeight: "calc(100vh - 64px)",
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: `1px solid ${grey[300]}`,
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          py: 1.5,
        }}
      >
        <MDTypography variant="h5">{label("assessment-report")}</MDTypography>
        <IconButton
          onClick={handleClose}
          disabled={loading.download}
          sx={{ p: 0 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: "0 !important",
          backgroundColor: "whitesmoke !important",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          position: "relative",
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          overflow: { xs: "auto", md: "hidden" },
        }}
      >
        <Box
          sx={{
            flex: { xs: "0 0 auto", md: "0 0 280px" },
            minWidth: 0,
            backgroundColor: "white !important",
            borderRight: `1px solid ${grey[300]}`,
            overflowY: { md: "auto" },
          }}
        >
          <MDTypography variant="h6" sx={{ px: 2, py: 1.5, pb: 1 }}>
            {label("setting-plural")}
          </MDTypography>

          <Grid container spacing={0}>
            <Grid item xs={12} sx={{ px: 2, py: 1 }}>
              <TextField
                select
                fullWidth
                label={label("kpi-pictures-per-row") || "KPI pictures per row"}
                value={options.photosPerRow}
                onChange={(event) => changeOptions("photosPerRow", event.target.value === "none" ? "none" : Number(event.target.value))}
              >
                <MenuItem value="none">{label("none") || "None"}</MenuItem>
                {[1, 2, 3, 4].map((count) => (
                  <MenuItem key={count} value={count}>{count === 1 ? "1 (default)" : count}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sx={{ px: 1 }}>
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Checkbox
                    checked={options.includeAssignee}
                    onChange={(e) =>
                      changeOptions("includeAssignee", e.target.checked)
                    }
                  />
                }
                label={label("show-assignee")}
              />
            </Grid>

            <Grid item xs={12} sx={{ px: 1 }}>
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Checkbox
                    checked={options.includeType}
                    onChange={(e) =>
                      changeOptions("includeType", e.target.checked)
                    }
                  />
                }
                label={label("show-audit-type")}
              />
            </Grid>

            <Grid item xs={12} sx={{ px: 1 }}>
              <Tooltip
                title={
                  isTimeDisabled ? "You need to save the audit time first" : ""
                }
                disableHoverListener={!isTimeDisabled}
              >
                <Box>
                  <FormControlLabel
                    sx={{ m: 0 }}
                    disabled={isTimeDisabled}
                    control={
                      <Checkbox
                        checked={options.includeTime !== ""}
                        onChange={handleTimeCheckbox}
                      />
                    }
                    label={label("show-time")}
                  />
                </Box>
              </Tooltip>
            </Grid>

            <Grid item xs={12} sx={{ px: 2, py: 1, mb: 1 }}>
              <TextField
                label="Type of visit"
                value={value}
                onChange={handleChange(async (value) => {
                  changeOptions("includeTypeOfVisit", value);
                })}
                fullWidth
              />
            </Grid>

            <Grid
              item
              xs={12}
              sx={{ borderBottom: `1px solid ${grey[300]}` }}
            />

            <Grid item xs={12} sx={{ px: 2, py: 1.5, pb: 1 }}>
              <MDTypography variant="h6" sx={{ mb: 1 }}>
                {label("download-and-share")}
              </MDTypography>
              <Box>
                <LoadingButton
                  onClick={() => {
                    generatePDF();
                  }}
                  loading={loading.download}
                  size="small"
                  variant="gradient"
                  color="info"
                >
                  {label("download-pdf-button")}
                </LoadingButton>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {audit.auditTypeId === 2 && html && (
          <Box
            sx={{
              flex: { xs: "0 0 60vh", md: 1 },
              minWidth: 0,
              minHeight: 0,
              overflow: "auto",
            }}
          >
            <iframe
              title="Embedded Content"
              onLoad={() => {
                changeLoading("html", false);
              }}
              onLoadedData={() => {
                changeLoading("html", false);
              }}
              srcDoc={html}
              style={{
                display: "block",
                // Reserve room for the iframe's vertical scrollbar so its document
                // retains the 800px layout viewport used for PDF rendering.
                width: "820px",
                height: "100%",
                border: "none",
              }}
            />
          </Box>
        )}

        {loading.html ? (
          <Box sx={{ position: "absolute", top: 10, right: 10, zIndex: 10 }}>
            <CircularProgress color="info" />
          </Box>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
